/* eslint-disable no-console */
/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import PropTypes from "prop-types";

import { useState, useEffect } from "react";
import { connect, useSelector } from "react-redux";
import moment from "moment";
import "moment/locale/es";
import { useTranslation } from "react-i18next";

// react-router components
import { Routes, Route, useLocation, useNavigate, Outlet } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";

// Material Dashboard 2 PRO React examples
import Sidenav from "components/Sidenav";
import Configurator from "components/Configurator";

// Material Dashboard 2 PRO React themes
import theme from "assets/theme";

// Material Dashboard 2 PRO React Dark Mode themes
import themeDark from "assets/theme-dark";

// Material Dashboard 2 PRO React routesMainUser
import routesMainUser from "routesMainUser";
import routesClient from "routesClient";

// Material Dashboard 2 PRO React contexts
import { useMaterialUIController, setMiniSidenav, setOpenConfigurator } from "context";
import { getIdToken, getAuth, onAuthStateChanged } from "firebase/auth";

// Images
import brandIcon from "assets/images/logo-blanco.svg";

// Actions
import { updateMainUser, initClient, initMainUserSocket } from "store/actions/mainUserActions";

import ProtectedRouteMainUser from "ProtectedRouteMainUser";
import ProtectedRouteClient from "ProtectedRouteClient";
import SignInCover from "layouts/authentication/sign-in/cover";
import SignUpCover from "layouts/authentication/sign-up/cover";
import ValidationPending from "layouts/authentication/sign-up/validationPending";
import Project from "layouts/project";
import OnnaDevice from "layouts/onnaDevice";
import Apartment from "layouts/apartment/mainUser";
import NewAmenityEvent from "layouts/amenitiesEvents/NewAmenityEvent";
import NewServiceEvent from "layouts/servicesEvents/NewServiceEvent";
import Database from "utils/database";
import Notifications from "components/Notifications";
import ClientReservationSelector from "components/ClientReservationSelector";
import historyHelper from "historyHelper";

import "./i18n";
import { endSocket } from "store/actions/socketActions";

const database = new Database();

function App({
  updateUserAction,
  initClientAction,
  actInitMainUserSocket,
  user,
  projects,
  sendEndSocket,
}) {
  const { i18n } = useTranslation();
  const { isValidated, isMainUser, isClient, isProjectManager } = user;

  const clientReservationSelectorOpen = useSelector(
    (state) => state.mainUserReducer?.clientReservationSelection?.isOpen || false
  );

  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, direction, layout, openConfigurator, sidenavColor, darkMode } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const location = useLocation();
  const { pathname } = location;
  const navigate = useNavigate();

  useEffect(() => {
    historyHelper.navigate = navigate;
    historyHelper.location = location;
  }, []);

  useEffect(() => {
    if (clientReservationSelectorOpen) {
      return;
    }

    if (isValidated && (isMainUser || isProjectManager)) {
      const isAuthPath =
        pathname.indexOf("/authentication/") === 0 || pathname === "/client/sign-in";

      if (isAuthPath) {
        navigate("/", { replace: true });
      }
    } else if (!isValidated && isMainUser) {
      navigate("/authentication/sign-up/validationPending", { replace: true });
    } else if (isValidated && isClient) {
      const isClientPath = pathname.indexOf("/client/") === 0;
      if (!isClientPath || pathname === "/client/sign-in") {
        navigate("/client/apartment", { replace: true });
      }
    }
  }, [
    isValidated,
    isMainUser,
    isProjectManager,
    isClient,
    clientReservationSelectorOpen,
    pathname,
  ]);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting auth state
  useEffect(() => {
    const auth = getAuth();
    onAuthStateChanged(auth, (newUser) => {
      if (newUser) {
        // User is signed in
        getIdToken(newUser).then((token) => {
          const { uid, auth: gotAuth } = newUser;
          database.checkUserValidation(uid).then((validated) => {
            const {
              validation,
              mainUser,
              isProjectManager: validatedIsProjectManager = false,
              mainUserUid = null,
              isAClient = false,
              projectsProjectManager = null,
              projectManagerDisplayName = null,
              projectManagerFeatures = null,
            } = validated;
            // console.log("validated", validated);
            if (mainUser) {
              if (validation) {
                actInitMainUserSocket({
                  uid,
                  token,
                  cb: (socketCreated) => {
                    if (socketCreated) {
                      updateUserAction({
                        uid,
                        email: gotAuth?.currentUser?.email,
                        isMainUser: true,
                        isProjectManager: false,
                        projectManagerUid: null,
                        isClient: false,
                        isValidated: true,
                        token,
                      });

                      database.start(uid);

                      initClientAction({
                        uid,
                        email: gotAuth?.currentUser?.email,
                        token,
                        isMainUser: true,
                      });
                    }
                  },
                });
              } else {
                // User is not validated
                actInitMainUserSocket({
                  uid,
                  token,
                  cb: (socketCreated) => {
                    if (socketCreated) {
                      initClientAction({ uid, email: gotAuth?.currentUser?.email, token });
                    }
                  },
                });

                database.stop();
                updateUserAction({
                  uid: "",
                  isMainUser: true,
                  isProjectManager: false,
                  projectManagerUid: null,
                  isClient: false,
                  isValidated: false,
                  token,
                });
                navigate("/authentication/sign-up/validationPending", { replace: true });
              }
            } else if (validatedIsProjectManager && mainUserUid && projectsProjectManager) {
              actInitMainUserSocket({
                uid,
                token,
                isProjectManager: validatedIsProjectManager,
                mainUserUid,
                cb: (socketCreated) => {
                  if (socketCreated) {
                    updateUserAction({
                      uid: mainUserUid,
                      projectManagerUid: uid,
                      email: gotAuth?.currentUser?.email,
                      isMainUser: false,
                      isProjectManager: true,
                      isClient: false,
                      isValidated: true,
                      projects: projectsProjectManager,
                      projectManagerDisplayName,
                      projectManagerFeatures,
                      token,
                    });

                    database.start(
                      mainUserUid,
                      validatedIsProjectManager,
                      projectsProjectManager,
                      projectManagerFeatures
                    );

                    initClientAction({
                      uid,
                      email: gotAuth?.currentUser?.email,
                      token,
                      isMainUser: false,
                      isProjectManager: true,
                      projectManagerUid: uid,
                    });
                  }
                },
              });
            } else if (isAClient) {
              actInitMainUserSocket({
                uid,
                token,
                cb: (socketCreated) => {
                  if (socketCreated) {
                    initClientAction({ uid, email: gotAuth?.currentUser?.email, token });
                  }
                },
              });
            }
          });
        });
      } else {
        database.stop();
        sendEndSocket();
        updateUserAction({
          uid: "",
          isValidated: false,
          isMainUser: false,
          isProjectManager: false,
          projectManagerUid: null,
          isClient: false,
        });
        if (pathname.indexOf("/client/") === 0) {
          navigate("/client/sign-in", { replace: true });
        } else {
          navigate("/authentication/sign-in/cover", { replace: true });
        }
      }
    });
  }, []);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  useEffect(() => {
    const { language = "es" } = user;
    moment.locale(language);
    i18n.changeLanguage(language);
  }, [user]);

  const getHidedRoutes = () => (
    <>
      <Route exact path="/authentication/sign-in/cover" element={<SignInCover />} />
      <Route exact path="/authentication/sign-up/cover" element={<SignUpCover />} />
      <Route
        exact
        path="/authentication/sign-up/validationPending"
        element={<ValidationPending />}
      />
      <Route path="/projects/:projectId" element={<Outlet />}>
        <Route index element={<Project database={database} />} />
        <Route path="onnaDevice/:onnaId" element={<OnnaDevice database={database} />} />
        <Route path="apartment/:onnaId/:apartmentId" element={<Apartment database={database} />} />
        <Route path="amenity/:uid" element={<NewAmenityEvent database={database} />} />
        <Route
          path="amenity/:uid/apartmentId/:apartmentId"
          element={<NewAmenityEvent database={database} />}
        />
        <Route path="service/:uid" element={<NewServiceEvent database={database} />} />
        <Route
          path="service/:uid/apartmentId/:apartmentId"
          element={<NewServiceEvent database={database} />}
        />
        <Route path=":index1" element={<Project database={database} />} />
        <Route path=":index1/:index2" element={<Project database={database} />} />
      </Route>
    </>
  );

  const getRoutesMainUser = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.route) {
        const Component = route.component;
        return (
          <Route
            exact
            path={route.route}
            key={route.key}
            element={
              <ProtectedRouteMainUser user={user} Component={<Component database={database} />} />
            }
          />
        );
      }
      return null;
    });

  const getRoutesClient = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.route) {
        const Component = route.component;
        return (
          <Route
            exact
            path={route.route}
            key={route.key}
            element={
              <ProtectedRouteClient user={user} Component={<Component database={database} />} />
            }
          />
        );
      }
      return null;
    });

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  );

  return (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {isValidated && layout === "dashboard" ? (
        <>
          <Sidenav
            color={sidenavColor}
            brand={brandIcon}
            routes={isClient ? routesClient(user) : routesMainUser(user, projects)}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
          <Configurator />
          {configsButton}
        </>
      ) : (
        <div />
      )}
      {isValidated && layout === "vr" ? <Configurator /> : <div />}
      <Routes>
        {getRoutesMainUser(routesMainUser(user, projects))}
        {getRoutesClient(routesClient(user))}
        {getHidedRoutes()}
      </Routes>
      <Notifications />
      <ClientReservationSelector />
    </ThemeProvider>
  );
}

App.defaultProps = {
  user: {},
  projects: {},
};

App.propTypes = {
  updateUserAction: PropTypes.func.isRequired,
  initClientAction: PropTypes.func.isRequired,
  actInitMainUserSocket: PropTypes.func.isRequired,
  sendEndSocket: PropTypes.func.isRequired,
  user: PropTypes.object,
  projects: PropTypes.object,
};

const mapStateToProps = (state) => ({
  user: state.mainUserReducer,
  projects: state.projectsReducer,
});

const mapDispatchToProps = (dispatch) => ({
  updateUserAction: (uid) => dispatch(updateMainUser(uid)),
  initClientAction: (uid) => dispatch(initClient(uid)),
  actInitMainUserSocket: (data) => dispatch(initMainUserSocket(data)),
  sendEndSocket: () => {
    dispatch(endSocket());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(App);
